// src/pages/wizard/Step5Review.jsx
import React, { useState } from 'react';
import { CheckCircle, AlertCircle, Loader2, GitBranch, Key, Database, Clock } from 'lucide-react';
import { apiCall } from '../../utils/api';
import { CONNECTOR_ENTITIES } from './Step4Entities.jsx';

export default function Step5Review({ data, onConnect, onError }) {
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('');

  // Auto-derive a default title from connector + auth context. User can
  // override before clicking Connect. Examples:
  //   "GitHub · acme"
  //   "Jira · acme.atlassian.net"
  //   "Slack · Acme Workspace"
  const defaultTitle = React.useMemo(() => {
    const base = data.connector?.name || data.connector?.id || 'Connector';
    const k = (data.connector?.id || data.connector?.key || '').toLowerCase();
    let suffix = '';
    if (k === 'github') {
      suffix = data.auth?.org || data.scope?.[0]?.id || data.scope?.[0]?.name || '';
    } else if (k === 'jira') {
      suffix = data.auth?.site || '';
    } else if (k === 'slack') {
      suffix = data.auth?.workspace || '';
    } else if (k === 'teams') {
      suffix = data.auth?.tenantId ? `tenant ${data.auth.tenantId.slice(0, 8)}` : '';
    } else if (k === 'namely') {
      suffix = data.auth?.subdomain || '';
    }
    return suffix ? `${base} · ${suffix}` : base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [title, setTitle] = useState(defaultTitle);

  const handleConnect = async () => {
    setConnecting(true);
    setError(null);
    setStatus('Finalizing connector...');



    try {
      // The connector_config was already created during Step 2 (OAuth or PAT).
      // We just need its ID, the auth state, and the user's selected entities.
      const configId = data.configId || data.auth?.configId;
      if (!configId) {
        throw new Error('Missing config_id from Step 2. Please go back and re-authenticate.');
      }

      // Build the config_json patch with everything Step 2 plus the new entity selections.
      // We keep server-set fields like team_id (Slack), site_name (Jira), etc. by merging
      // with what's already there server-side. Cleanest is to PATCH only the user-driven fields.
      // Step 1: PATCH the existing config to add entity selections / scope
      setStatus('Saving sync preferences...');
      const finalTitle = (title && title.trim()) ? title.trim() : defaultTitle;
      const patchRes = await apiCall(`/sync/configs/${configId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          display_name: finalTitle,
          entities: data.entities,
          sync_all_entities: false,
        }),
      });


      if (!patchRes.ok) {
        const errorData = await patchRes.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update connector configuration');
      }

      // Step 1.5: For connectors that support field discovery (currently Jira),
      // populate the connector_field table so the field mapping page is ready
      // immediately. Best-effort: if discovery fails we still proceed with sync.
      const connectorKey = (data.connector?.id || data.connector?.key || '').toLowerCase();
      if (connectorKey === 'jira') {
        setStatus('Discovering fields...');
        try {
          const discoverRes = await apiCall(
            `/connector-fields/configs/${configId}/discover`,
            { method: 'POST' }
          );
          if (discoverRes.ok) {
            const discoverData = await discoverRes.json();
            if (discoverData.error) {
              console.warn('Field discovery returned error:', discoverData.error);
            } else {
              console.log(
                `Discovered ${discoverData.discovered} field(s), ` +
                `inserted ${discoverData.inserted}, updated ${discoverData.updated}`
              );
            }
          } else {
            console.warn(`Field discovery HTTP ${discoverRes.status}`);
          }
        } catch (discoverErr) {
          // Non-fatal: customer can re-run discovery from the field mapping page
          console.warn('Field discovery failed (non-fatal):', discoverErr);
        }
      }

      // Step 2: Trigger initial sync for each selected entity

      // Order entities so dependencies sync before dependents (raw ->
      // derived -> kpis). The CONNECTOR_ENTITIES list for each connector is
      // declared in dependency order, so sorting selected entities by their
      // index in that list reproduces a valid run order. Entities not in the
      // list (or connectors without a list) keep their original order.
      const orderList = (CONNECTOR_ENTITIES[connectorKey] || []).map((e) => e.id);
      const orderIndex = (entity) => {
        const i = orderList.indexOf(entity);
        return i === -1 ? Number.MAX_SAFE_INTEGER : i;
      };
      const orderedEntities = [...data.entities].sort(
        (a, b) => orderIndex(a) - orderIndex(b)
      );

      const syncResults = [];
      for (const entity of orderedEntities) {
        setStatus(`Syncing ${entity}...`);

        try {
          const syncRes = await apiCall('/sync/run', {
            method: 'POST',
            body: JSON.stringify({
              config_id: configId,
              entity: entity,
              run_type: "wizard",
            }),
          });

          const syncData = await syncRes.json();
          syncResults.push({
            entity,
            success: syncData.status === 'ok',
            result: syncData,
          });
        } catch (syncErr) {
          console.error(`Failed to sync ${entity}:`, syncErr);
          syncResults.push({
            entity,
            success: false,
            error: syncErr.message,
          });
        }
      }

      const successCount = syncResults.filter(r => r.success).length;
      setStatus(`Complete. Synced ${successCount}/${data.entities.length} entities.`);

      setTimeout(() => {
        if (onConnect) {
          onConnect({ id: configId }, syncResults);
        }
      }, 1500);

    } catch (err) {
      console.error('Connection failed:', err);
      setError(err.message || 'Failed to connect');
      setStatus('');

      if (onError) {
        onError(err);
      }
    } finally {
      setConnecting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white mb-2">
          Review and Connect
        </h2>
        <p className="text-sm text-[#a0a0a0]">
          Review your configuration and start syncing from {data.connector?.name}
        </p>
      </div>

      <div className="space-y-4">
        <div className="p-4 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <GitBranch className="w-4 h-4 text-blue-500" />
              <h3 className="text-sm font-medium text-white">Connector</h3>
            </div>
            {(data.configId || data.auth?.configId) && (
              <span className="text-xs text-[#6a6a6a] font-mono">
                ID #{data.configId || data.auth?.configId}
              </span>
            )}
          </div>
          <div className="ml-7 space-y-2">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={defaultTitle}
              className="w-full bg-[#0f0f0f] border border-[#2a2a2a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
              maxLength={100}
              disabled={connecting}
            />
            <p className="text-xs text-[#6a6a6a]">
              Choose a name to recognize this connector by. You can rename it later.
            </p>
          </div>
        </div>

        <div className="p-4 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl">
          <div className="flex items-center gap-3 mb-2">
            <Key className="w-4 h-4 text-blue-500" />
            <h3 className="text-sm font-medium text-white">Authentication</h3>
          </div>
          <div className="ml-7 space-y-1">
            {data.auth?.org && (
              <p className="text-sm text-[#a0a0a0]">
                Organization: <span className="text-white">{data.auth.org}</span>
              </p>
            )}
            {data.auth?.workspace && (
              <p className="text-sm text-[#a0a0a0]">
                Workspace: <span className="text-white">{data.auth.workspace}</span>
              </p>
            )}
            {data.auth?.site && (
              <p className="text-sm text-[#a0a0a0]">
                Site: <span className="text-white">{data.auth.site}</span>
              </p>
            )}
            <p className="text-sm text-[#a0a0a0]">
              Credentials: <span className="text-white">Configured</span>
            </p>
          </div>
        </div>

        <div className="p-4 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl">
          <div className="flex items-center gap-3 mb-2">
            <Database className="w-4 h-4 text-blue-500" />
            <h3 className="text-sm font-medium text-white">Entities to Sync</h3>
          </div>
          <div className="ml-7 flex flex-wrap gap-2">
            {data.entities.map((entity) => (
              <span
                key={entity}
                className="px-2 py-1 bg-blue-500/10 border border-blue-500/20 rounded text-xs text-blue-400"
              >
                {entity}
              </span>
            ))}
          </div>
        </div>

        {data.scope && data.scope.length > 0 && (
          <div className="p-4 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl">
            <div className="flex items-center gap-3 mb-2">
              <Clock className="w-4 h-4 text-blue-500" />
              <h3 className="text-sm font-medium text-white">Access Scope</h3>
            </div>
            <div className="ml-7">
              <p className="text-sm text-[#a0a0a0]">
                {data.scope.length} scope{data.scope.length !== 1 ? 's' : ''} configured
              </p>
            </div>
          </div>
        )}
      </div>

      {connecting && status && (
        <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
          <p className="text-sm text-blue-400">{status}</p>
        </div>
      )}

      {!connecting && status && !error && (
        <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-xl flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-400" />
          <p className="text-sm text-green-400">{status}</p>
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-red-400 font-medium">Connection Failed</p>
            <p className="text-xs text-red-400 mt-1">{error}</p>
          </div>
        </div>
      )}

      {!connecting && !status && (
        <button
          onClick={handleConnect}
          disabled={connecting}
          className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
        >
          <CheckCircle className="w-5 h-5" />
          Connect and Start Syncing
        </button>
      )}
    </div>
  );
}

// Builds a partial config_json patch that only contains the user's wizard
// selections (entities, sync_all_entities). Server-side OAuth-set fields
// like team_id, site_name, cloud_id are already in config_json from Step 2
// and stay untouched.

